import z from 'zod';
import type { ErpWorkspaceOnboardingStatus } from './workspace-onboarding';
import type { ErpWorkspaceSubscription } from './saas';
import { ErpInteractionIntentContextSchema, type ErpInteractionIntentContext } from './context-runtime';

export type ErpId = string;
export type ErpIsoDate = string;

export * from './desktop-board';
export * from './guest-access';
export * from './document-template';
export * from './feedback';
export * from './resource-dt-ingestion';
export * from './context-runtime';
export * from './resource-dt-share';
export * from './system-actors';
export * from './workspace-onboarding';
export * from './saas';
export * from './self-guided-onboarding';
export * from './activity';
export * from './kanban';

export interface ErpActionTarget {
    type: string;
    id: string;
    label?: string;
}

export const ErpActionTargetSchema = z
    .object({
        type: z.string().trim().min(1).max(80),
        id: z.string().trim().min(1).max(200),
        label: z.string().max(200).optional(),
    })
    .strict();

export type ErpActionSource = 'button' | 'spotlight' | 'assistant' | 'rest' | 'mcp' | 'webhook' | 'automation';
export type ErpActionChannel = 'ui' | 'action-bar' | 'assistant' | 'mcp' | 'webhook' | 'automation' | 'api';
export type ErpActionAttachmentCardinality = 'one' | 'many';

type ErpActionInputFieldMetadata = {
    name: string;
    label: string;
    description?: string;
    required?: boolean;
    purpose?: 'value' | 'instruction' | 'evidence';
};

export type ErpActionInputField =
    | ({ type: 'string'; defaultValue?: string; binding?: 'current-user-message' } & ErpActionInputFieldMetadata)
    | ({ type: 'textarea'; defaultValue?: string; binding?: 'current-user-message' } & ErpActionInputFieldMetadata)
    | ({ type: 'number'; defaultValue?: number; binding?: never } & ErpActionInputFieldMetadata)
    | ({ type: 'boolean'; defaultValue?: boolean; binding?: never } & ErpActionInputFieldMetadata)
    | ({ type: 'date'; defaultValue?: string; binding?: never } & ErpActionInputFieldMetadata)
    | ({
          type: 'select';
          options: Array<{ value: string; label: string }>;
          defaultValue?: string;
          binding?: never;
      } & ErpActionInputFieldMetadata)
    | ({
          type: 'multiselect';
          options: Array<{ value: string; label: string }>;
          defaultValue?: string[];
          binding?: never;
      } & ErpActionInputFieldMetadata);

export interface ErpActionInputSchema {
    fields: ErpActionInputField[];
}

export interface ErpActionAttachmentSemanticType {
    label: string;
    filenameHints?: string[];
}

export interface ErpActionAttachmentDefinition {
    label: string;
    required: boolean;
    cardinality?: ErpActionAttachmentCardinality;
    /** Technical file formats accepted by this attachment slot. */
    acceptedMimeTypes?: string[];
    acceptedExtensions?: string[];
    /** Optional semantic vocabulary; documents may remain untyped unless constrained elsewhere. */
    semanticTypes?: Record<string, ErpActionAttachmentSemanticType>;
}

export interface ErpActionEvidenceDefinition {
    minimumSources: number;
    acceptedSources: Array<'attachment' | 'linked-document' | 'text-input'>;
}

const erpActionPresenceControlIdSchema = z.string().trim().min(1).max(160);

export const ErpActionPresenceTargetHintSchema = z
    .object({
        kind: z.enum(['field', 'control']),
        controlId: erpActionPresenceControlIdSchema,
    })
    .strict();

export type ErpActionPresenceTargetHint = z.infer<typeof ErpActionPresenceTargetHintSchema>;

const erpActionPresenceInputTargetsSchema = z
    .record(z.string().trim().min(1).max(80), ErpActionPresenceTargetHintSchema)
    .superRefine((targets, context) => {
        if (Object.keys(targets).length > 32) {
            context.addIssue({
                code: z.ZodIssueCode.too_big,
                maximum: 32,
                origin: 'array',
                inclusive: true,
                message: 'Too many Presence input targets.',
            });
        }
    });

export const ErpActionPresenceInteractionSchema = z
    .object({
        inputTargets: erpActionPresenceInputTargetsSchema.optional(),
        executeTarget: z
            .object({ kind: z.literal('control'), controlId: erpActionPresenceControlIdSchema })
            .strict()
            .optional(),
    })
    .strict();

export type ErpActionPresenceInteraction = z.infer<typeof ErpActionPresenceInteractionSchema>;

export const ErpActionInteractionSchema = z
    .object({
        presence: ErpActionPresenceInteractionSchema.optional(),
    })
    .strict();

export type ErpActionInteraction = z.infer<typeof ErpActionInteractionSchema>;

export type ErpActionOutputField =
    | { type: 'string'; name: string; description?: string }
    | { type: 'number'; name: string; description?: string }
    | { type: 'boolean'; name: string; description?: string }
    | { type: 'object'; name: string; description?: string; fields?: ErpActionOutputField[] }
    | { type: 'array'; name: string; description?: string; items?: ErpActionOutputField };

export interface ErpActionOutputSchema {
    fields: ErpActionOutputField[];
}

/** Semantic classification for execution-history projections; distinct from discovery relevance. */
export type ErpActionHistoryNature = 'business' | 'utility' | 'technical';

export interface ErpActionDefinition {
    id: string;
    family: string;
    label: string;
    description?: string;
    keywords?: string[];
    targets: Record<
        string,
        {
            label: string;
            required: boolean;
            cardinality: 'one' | 'many';
            acceptedTypes: string[];
        }
    >;
    inputSchema?: ErpActionInputSchema;
    interaction?: ErpActionInteraction;
    outputSchema?: ErpActionOutputSchema;
    attachments?: Record<string, ErpActionAttachmentDefinition>;
    /** Declares the minimum evidence an implementation must resolve during prepare. */
    evidence?: ErpActionEvidenceDefinition;
    result: {
        kind: 'data' | 'file' | 'navigation' | 'void';
    };
    history?: {
        nature: ErpActionHistoryNature;
    };
    /** Entry points allowed to discover and prepare this Action. */
    exposure: {
        channels: ErpActionChannel[];
    };
}

export interface ErpActionAttachmentReference {
    id: string;
    slot: string;
    originalName: string;
    mimeType: string;
    size: number;
    /** Runtime-resolved semantic type declared by the Action attachment slot. */
    semanticType?: string;
}

export type ErpActionAttachmentReferences = ErpActionAttachmentReference | ErpActionAttachmentReference[];

export interface PrepareErpActionRequest {
    actionId: string;
    source: ErpActionSource;
    prefill?: {
        targets?: Record<string, ErpActionTarget | ErpActionTarget[]>;
        input?: Record<string, unknown>;
        attachments?: Record<string, ErpActionAttachmentReferences>;
    };
    context?: {
        navigation?: {
            route?: string;
            currentTargets?: ErpActionTarget[];
        };
        /** Board context used only for server-owned execution Presence. */
        presenceContextId?: string;
    };
}

/** Public HTTP payload. The server derives `source` from the authenticated entrypoint. */
export type PrepareErpActionPayload = Omit<PrepareErpActionRequest, 'source'>;

const erpActionAttachmentReferenceSchema = z.object({
    id: z.string().min(1),
    slot: z.string().min(1),
    originalName: z.string(),
    mimeType: z.string(),
    size: z.number().nonnegative(),
    semanticType: z.string().optional(),
}).strict();

/** Validates public input. Entry-point provenance is supplied by the server, never by this payload. */
function stripClientActionSource(value: unknown): unknown {
    if (value === null || typeof value !== 'object' || Array.isArray(value)) return value;
    const payload = { ...(value as Record<string, unknown>) };
    delete payload.source;
    return payload;
}

export const PrepareErpActionRequestSchema = z.preprocess(stripClientActionSource, z.object({
    actionId: z.string().trim().min(1).max(200),
    prefill: z.object({
        targets: z.record(z.string(), z.union([ErpActionTargetSchema, z.array(ErpActionTargetSchema)])).optional(),
        input: z.record(z.string(), z.unknown()).optional(),
        attachments: z.record(z.string(), z.union([erpActionAttachmentReferenceSchema, z.array(erpActionAttachmentReferenceSchema)])).optional(),
    }).strict().optional(),
    context: z.object({
        navigation: z.object({
            route: z.string().max(500).optional(),
            currentTargets: z.array(ErpActionTargetSchema).max(32).optional(),
        }).strict().optional(),
        presenceContextId: z.string().max(200).optional(),
    }).strict().optional(),
}).strict());

export interface ErpActionNavigationContext {
    route?: string;
    currentTargets?: ErpActionTarget[];
}

const erpActionNavigationContextSchema = z
    .object({
        route: z.string().max(500).optional(),
        currentTargets: z.array(ErpActionTargetSchema).max(32).optional(),
    })
    .strict();

export type ErpActionDiscoveryPurpose = 'action-discovery' | 'assistant-action-selection';

export interface DiscoverErpActionsRequest {
    source: ErpActionSource;
    purpose?: ErpActionDiscoveryPurpose;
    context?: {
        navigation?: ErpActionNavigationContext;
        intent?: ErpInteractionIntentContext;
    };
}

/** Public HTTP discovery payload. The server derives the consumer from its route. */
export type DiscoverErpActionsPayload = Omit<DiscoverErpActionsRequest, 'source'>;

export const DiscoverErpActionsRequestSchema = z
    .preprocess(stripClientActionSource, z.object({
        purpose: z.enum(['action-discovery', 'assistant-action-selection']).optional(),
        context: z
            .object({
                navigation: erpActionNavigationContextSchema.optional(),
                intent: ErpInteractionIntentContextSchema.optional(),
            })
            .strict()
            .optional(),
    }).strict());

export type ErpActionRelevance = 'contextual' | 'global' | 'partial' | 'unresolved';

export interface ErpActionDiscoveryPresentation {
    pinned: boolean;
    promoted: boolean;
}

export interface ErpActionAttachmentMatch {
    slot: string;
    cardinality: ErpActionAttachmentCardinality;
    sourceIds: string[];
    resolvedSemanticTypes?: Array<{ sourceId: string; semanticType: string }>;
}

export interface ErpActionDiscoveryItem {
    action: ErpActionDefinition;
    relevance: ErpActionRelevance;
    matchedTargetRoles: string[];
    missingTargetRoles: string[];
    /** Descriptive source matches only; prepare remains responsible for strict binding and validation. */
    matchedAttachmentSlots: string[];
    matchedSourceIds: string[];
    /** Explicit source-to-slot projection owned by ERP Action Discovery. */
    attachmentMatches?: ErpActionAttachmentMatch[];
    presentation: ErpActionDiscoveryPresentation;
}

export interface DiscoverErpActionsResponse {
    data: ErpActionDiscoveryItem[];
}

export interface ResolveErpContactsRequest {
    reference?: string;
    email?: string;
    phone?: string;
    companyId?: ErpId;
}

export interface ResolvedErpContact {
    id: ErpId;
    firstName?: string;
    lastName?: string;
    email?: string;
    phone?: string;
    companyId?: ErpId | null;
}

export type ErpEntityResolution<TCandidate> =
    | { status: 'not-found'; candidates: [] }
    | { status: 'unique'; candidate: TCandidate }
    | { status: 'ambiguous'; candidates: TCandidate[] };

export interface ResolveErpContactsResponse {
    resolution: ErpEntityResolution<ResolvedErpContact>;
}

export type ErpResolvedValueSource = 'explicit' | 'navigation-context' | 'assistant-proposal' | 'default';

export interface ErpResolvedActionTarget {
    value: ErpActionTarget | ErpActionTarget[];
    source: ErpResolvedValueSource;
}

export interface ErpResolvedActionValues {
    targets: Record<string, ErpResolvedActionTarget>;
    input: Record<string, { value: unknown; source: ErpResolvedValueSource }>;
}

export type ErpContextKey = string;
export type ErpContextPurpose = string;
export type ErpContextSourceKind = 'authoritative' | 'user' | 'inferred' | 'external';
export type ErpContextSensitivity = 'public' | 'internal' | 'sensitive' | 'restricted';

export interface ErpContextCapability {
    key: ErpContextKey;
}

export interface ErpContextRequest {
    purpose: ErpContextPurpose;
    requires: readonly ErpContextKey[];
    optional?: readonly ErpContextKey[];
}

export interface ErpContextSource {
    provider: string;
    kind: ErpContextSourceKind;
}

export interface ErpContextFragment<T = unknown> {
    key: ErpContextKey;
    value: T;
    source: ErpContextSource;
    confidence?: number;
    observedAt: string;
    validAt?: string;
    expiresAt?: string;
    sensitivity?: ErpContextSensitivity;
}

export interface ErpContextSnapshot {
    purpose: ErpContextPurpose;
    requestedAt: string;
    fragments: readonly ErpContextFragment[];
}

export interface ErpIntegrationCapability {
    id: string;
    operations: readonly string[];
}

export interface ErpIntegrationCapabilitiesContext {
    version: 1;
    integrations: readonly ErpIntegrationCapability[];
}

export interface ErpActionConfirmation {
    id: string;
    title: string;
    message: string;
}

export type ErpActionRequirement =
    | {
          kind: 'target';
          /** Stable normalized key. Kept optional for dynamically supplied legacy requirements. */
          key?: string;
          role: string;
          label: string;
          required?: boolean;
          acceptedTypes: string[];
          cardinality: 'one' | 'many';
      }
    | {
          kind: 'input';
          /** Stable normalized key. Kept optional for dynamically supplied legacy requirements. */
          key?: string;
          label?: string;
          required?: boolean;
          field: ErpActionInputField;
      }
    | {
          kind: 'attachment';
          /** Stable normalized key. Kept optional for dynamically supplied legacy requirements. */
          key?: string;
          slot: string;
          label: string;
          required: boolean;
          cardinality: ErpActionAttachmentCardinality;
          acceptedMimeTypes?: string[];
          acceptedExtensions?: string[];
          semanticTypes?: Record<string, ErpActionAttachmentSemanticType>;
      }
    | {
          kind: 'evidence';
          key?: string;
          label: string;
          minimumSources: number;
          availableSources: number;
          acceptedSources: ErpActionEvidenceDefinition['acceptedSources'];
          inputNames?: string[];
      };

/** Fully normalized requirement shape emitted by the ActionDefinition projection. */
export type ErpNormalizedActionRequirement =
    | {
          kind: 'target';
          key: string;
          role: string;
          label: string;
          required: boolean;
          acceptedTypes: string[];
          cardinality: 'one' | 'many';
      }
    | {
          kind: 'input';
          key: string;
          label: string;
          required: boolean;
          field: ErpActionInputField;
      }
    | {
          kind: 'attachment';
          key: string;
          slot: string;
          label: string;
          required: boolean;
          cardinality: ErpActionAttachmentCardinality;
          acceptedMimeTypes?: string[];
          acceptedExtensions?: string[];
          semanticTypes?: Record<string, ErpActionAttachmentSemanticType>;
      };

export type PrepareErpActionResponse =
    | {
          status: 'ready';
          preparedActionId: string;
          action: ErpActionDefinition;
          resolved: ErpResolvedActionValues;
          expiresAt: string;
      }
    | {
          status: 'requires-input';
          action: ErpActionDefinition;
          resolved: ErpResolvedActionValues;
          missing: ErpActionRequirement[];
      }
    | {
          status: 'requires-disambiguation';
          action: ErpActionDefinition;
          resolved: ErpResolvedActionValues;
          reason: 'multiple-matches';
          requirements: ErpActionRequirement[];
      }
    | {
          status: 'requires-confirmation';
          preparedActionId: string;
          action: ErpActionDefinition;
          resolved: ErpResolvedActionValues;
          confirmation: ErpActionConfirmation;
          expiresAt: string;
      }
    | {
          status: 'unavailable';
          action: ErpActionDefinition;
          reason: string;
          code?: string;
      };

export interface ExecuteErpPreparedActionRequest {
    confirmation?: {
        id: string;
        accepted: true;
    };
}

export type ErpActionExecutionFact = { type: string; id: string; label?: string };
export interface ErpActionExecutionEffects {
    createdEntities?: ErpActionExecutionFact[];
    updatedEntities?: ErpActionExecutionFact[];
    archivedEntities?: ErpActionExecutionFact[];
}
export type ExecuteErpActionJsonResponse =
    | { kind: 'data'; data: unknown; effects?: ErpActionExecutionEffects }
    | { kind: 'navigation'; href: string; effects?: ErpActionExecutionEffects };

export interface ErpPaginationQuery {
    page?: string;
    limit?: string;
}

export interface ErpArchiveResponse {
    message: string;
}

export interface ErpRestoreResponse {
    message: string;
}

export interface ErpApplicationErrorResponse {
    statusCode: number;
    code: string;
    message: string;
    details?: unknown;
    requestId?: string;
}

export const erpPermissionScopes = ['own', 'team', 'all'] as const;
export type ErpPermissionScope = (typeof erpPermissionScopes)[number];

/** A permission key is stable; its scope controls the rows visible to its holder. */
export interface ErpPermissionAssignment {
    key: string;
    scope: ErpPermissionScope;
}

export const erpResourceTypes = ['candidate', 'consultant', 'staff'] as const;
export type ErpResourceType = (typeof erpResourceTypes)[number];

export type ErpResourceStatus = string;

export const erpResourceAvailabilityStatuses = [
    'available',
    'available_soon',
    'in_mission',
    'partially_available',
    'unavailable',
    'unknown',
] as const;
export type ErpResourceAvailabilityStatus = (typeof erpResourceAvailabilityStatuses)[number];

export const erpResourceStaffingPriorities = ['low', 'normal', 'high', 'urgent'] as const;
export type ErpResourceStaffingPriority = (typeof erpResourceStaffingPriorities)[number];

export const erpResourceRemotePreferences = ['onsite', 'hybrid', 'remote', 'flexible', 'unknown'] as const;
export type ErpResourceRemotePreference = (typeof erpResourceRemotePreferences)[number];

export const erpResourceWorkloadPreferences = ['full_time', 'part_time', 'three_days', 'four_days', 'flexible', 'unknown'] as const;
export type ErpResourceWorkloadPreference = (typeof erpResourceWorkloadPreferences)[number];

export const erpResourceUnavailabilityTypes = [
    'vacation',
    'sick_leave',
    'personal',
    'notice_period',
    'training',
    'pause',
    'unavailable',
    'other',
] as const;
export type ErpResourceUnavailabilityType = (typeof erpResourceUnavailabilityTypes)[number];

export const erpResourceUnavailabilityStatuses = ['planned', 'active', 'ended', 'cancelled'] as const;
export type ErpResourceUnavailabilityStatus = (typeof erpResourceUnavailabilityStatuses)[number];

export interface ErpResourceStatusOption {
    key: ErpResourceStatus;
    label: string;
    color?: string;
    order: number;
    active: boolean;
}

export type ErpContactPointType = 'personal' | 'work' | 'other';
export type ErpSocialLinkType = 'linkedin' | 'github' | 'website' | 'other';
export type ErpResourceContactPointType = ErpContactPointType;
export type ErpResourceSocialLinkType = ErpSocialLinkType;

export function classifyErpSocialLinkType(url: string): ErpSocialLinkType {
    try {
        const hostname = new URL(url).hostname.toLocaleLowerCase().replace(/^www\./u, '');
        if (hostname === 'linkedin.com' || hostname.endsWith('.linkedin.com')) return 'linkedin';
        if (hostname === 'github.com' || hostname.endsWith('.github.com')) return 'github';
        return 'website';
    } catch {
        return 'other';
    }
}

export interface ErpResourceIdentity {
    civility?: string;
    firstName?: string;
    lastName?: string;
    displayName?: string;
    title?: string;
    birthDate?: string | null;
    birthPlace?: string;
    photoDocumentId?: ErpId | null;
}

export interface ErpContactEmail {
    value: string;
    type?: ErpContactPointType;
    primary?: boolean;
}

export interface ErpContactPhone {
    value: string;
    type?: ErpContactPointType;
    primary?: boolean;
}

export interface ErpPostalAddress {
    line1?: string;
    line2?: string;
    zipCode?: string;
    city?: string;
    country?: string;
}

export interface ErpSocialLink {
    url: string;
    type?: ErpSocialLinkType;
    label?: string;
    primary?: boolean;
}

export interface ErpContactDetails {
    emails?: ErpContactEmail[];
    phones?: ErpContactPhone[];
    postalAddress?: ErpPostalAddress;
    socialLinks?: ErpSocialLink[];
}

export type ErpResourceContactEmail = ErpContactEmail;
export type ErpResourceContactPhone = ErpContactPhone;
export type ErpResourcePostalAddress = ErpPostalAddress;
export type ErpResourceSocialLink = ErpSocialLink;
export type ErpResourceContactDetails = ErpContactDetails;

export interface ErpOrganization {
    salesOwnerId?: ErpId | null;
    salesOwner?: string | null;
    influencerUserIds?: ErpId[];
    influencers?: string[];
    hrOwnerId?: ErpId | null;
    hrOwner?: string | null;
    agencyTeamId?: ErpId | null;
    agencyTeam?: string | null;
    businessUnitTeamId?: ErpId | null;
    businessUnitTeam?: string | null;
}

export type ErpResourceOrganization = ErpOrganization;

export type ErpReferentialRequirementType = 'required' | 'preferred' | 'nice_to_have';
export type ErpReferentialReferenceLevel = 'beginner' | 'intermediate' | 'advanced' | 'expert';

export interface ErpReferentialReference {
    referentialKey: string;
    itemKey: string;
    itemId?: string;
    label?: string;
    categoryIds?: string[];
    source?: 'neylia' | 'company';
    confirmed?: boolean;
    confidence?: number | null;
    primary?: boolean;
    weight?: number | null;
    requirementType?: ErpReferentialRequirementType;
    level?: ErpReferentialReferenceLevel;
    yearsOfExperience?: number | null;
    lastUsedAt?: string | null;
    notes?: string;
    customFields?: Record<string, unknown>;
}

export const erpCompanyStatuses = ['prospect', 'client', 'inactive'] as const;
export type ErpCompanyStatus = (typeof erpCompanyStatuses)[number];

export const erpCompanyPriorities = ['low', 'medium', 'high', 'strategic'] as const;
export type ErpCompanyPriority = (typeof erpCompanyPriorities)[number];

export const erpContactRelationshipStatuses = ['cold', 'warm', 'active', 'inactive'] as const;
export type ErpContactRelationshipStatus = (typeof erpContactRelationshipStatuses)[number];

export const erpNeedStatuses = ['draft', 'open', 'positioning', 'mission', 'closed', 'lost'] as const;
export type ErpNeedStatus = (typeof erpNeedStatuses)[number];

export const erpNeedStages = ['new', 'qualified', 'proposal', 'negotiation', 'won', 'lost'] as const;
export type ErpNeedStage = (typeof erpNeedStages)[number];

export const erpNeedPriorities = ['low', 'medium', 'high', 'urgent'] as const;
export type ErpNeedPriority = (typeof erpNeedPriorities)[number];

export const erpNeedOpportunityTypes = [
    'regie',
    'forfait',
    'centre_service',
    'audit',
    'conseil',
    'pre_embauche',
    'recrutement',
    'other',
] as const;
export type ErpNeedOpportunityType = (typeof erpNeedOpportunityTypes)[number];

export const erpNeedContractTypes = ['time_material', 'fixed_price', 'package', 'retainer', 'other'] as const;
export type ErpNeedContractType = (typeof erpNeedContractTypes)[number];

export const erpNeedQualificationStatuses = ['unqualified', 'to_qualify', 'qualified', 'disqualified'] as const;
export type ErpNeedQualificationStatus = (typeof erpNeedQualificationStatuses)[number];

export const erpNeedSalesStages = [
    'new',
    'qualification',
    'needs_analysis',
    'proposal',
    'negotiation',
    'verbal_agreement',
    'won',
    'lost',
    'paused',
] as const;
export type ErpNeedSalesStage = (typeof erpNeedSalesStages)[number];

export const erpNeedForecastCategories = ['pipeline', 'best_case', 'commit', 'closed', 'omitted'] as const;
export type ErpNeedForecastCategory = (typeof erpNeedForecastCategories)[number];

export const erpNeedLostReasons = [
    'price',
    'no_budget',
    'lost_to_competitor',
    'no_profile',
    'profile_rejected',
    'timeline_changed',
    'project_cancelled',
    'no_decision',
    'other',
] as const;
export type ErpNeedLostReason = (typeof erpNeedLostReasons)[number];

export const erpNeedSeniorities = ['junior', 'confirmed', 'senior', 'lead', 'expert', 'mixed'] as const;
export type ErpNeedSeniority = (typeof erpNeedSeniorities)[number];

export const erpPositioningStatuses = [
    'identified',
    'qualified',
    'internal_validation',
    'proposed',
    'sent',
    'interview',
    'interview_scheduled',
    'interview_done',
    'shortlisted',
    'accepted',
    'rejected',
    'cancelled',
    'withdrawn',
    'mission_started',
] as const;
export type ErpPositioningStatus = (typeof erpPositioningStatuses)[number];

export const erpMissionStatuses = ['draft', 'planned', 'active', 'renewal_pending', 'ended', 'cancelled'] as const;
export type ErpMissionStatus = (typeof erpMissionStatuses)[number];

export const erpMissionBillingModes = ['time_material', 'fixed_price', 'package', 'retainer', 'other'] as const;
export type ErpMissionBillingMode = (typeof erpMissionBillingModes)[number];

export const erpFinancialDataStatuses = ['complete', 'partial', 'missing', 'invalid'] as const;
export type ErpFinancialDataStatus = (typeof erpFinancialDataStatuses)[number];

export const erpMissionActualSources = ['manual', 'import', 'billing', 'other'] as const;
export type ErpMissionActualSource = (typeof erpMissionActualSources)[number];

export type ErpForecastCalculationMethod = 'daily_rate' | 'uniform_distribution' | 'single_month' | 'manual';
export type ErpForecastSourceType = 'mission' | 'opportunity' | 'renewal' | 'manual_adjustment';

export const erpDocumentEntityTypes = ['resource', 'company', 'contact', 'need', 'positioning', 'mission', 'activity', 'identity', 'workspace'] as const;
export type ErpDocumentEntityType = (typeof erpDocumentEntityTypes)[number];

export const erpDocumentTypes = [
    'cv',
    'dt',
    'skills_file',
    'certification',
    'identity',
    'administrative',
    'contract',
    'purchase_order',
    'proposal',
    'nda',
    'mission_file',
    'interview_report',
    'client_feedback',
    'hr',
    'billing',
    'profile_photo',
    'company_logo',
    'other',
] as const;
export type ErpDocumentType = (typeof erpDocumentTypes)[number];

export const erpDocumentStatuses = ['draft', 'active', 'expired', 'archived'] as const;
export type ErpDocumentStatus = (typeof erpDocumentStatuses)[number];

export const erpDocumentStorageProviders = ['external', 'local'] as const;
export type ErpDocumentStorageProvider = (typeof erpDocumentStorageProviders)[number];

export const erpActivityEntityTypes = [
    'company',
    'contact',
    'need',
    'resource',
    'positioning',
    'mission',
    'document',
    'activity',
    'team',
] as const;
export type ErpActivityEntityType = (typeof erpActivityEntityTypes)[number];

/** A business relation of an Activity. The vocabulary stays extensible by design. */
export interface ErpActivityRelation {
    entityType: string;
    entityId: ErpId;
    role?: string;
}

export interface ErpActivityAuthor {
    kind: 'human' | 'system';
    id: string;
}

export interface ErpActivityLink {
    url: string;
    label?: string;
}

export const erpActivityTypes = [
    'note',
    'task',
    'timeline',
    'attachment',
    'call',
    'email',
    'meeting',
    'follow_up',
    'interview',
    'feedback',
    'document',
    'status_change',
    'positioning',
    'mission',
    'system',
] as const;
export type ErpActivityType = (typeof erpActivityTypes)[number];

export const erpUserAuthoredActivityTypes = ['note', 'task', 'call', 'email', 'meeting', 'follow_up', 'interview', 'feedback'] as const;
export type ErpUserAuthoredActivityType = (typeof erpUserAuthoredActivityTypes)[number];

export interface CreateErpActivityTemplateRequest {
    name: string;
    type: ErpUserAuthoredActivityType;
    htmlContent: string;
}
export interface UpdateErpActivityTemplateRequest {
    name?: string;
    type?: ErpUserAuthoredActivityType;
    htmlContent?: string;
}
export interface ErpActivityTemplateResponse {
    id: ErpId;
    workspaceId: ErpId;
    name: string;
    type: ErpUserAuthoredActivityType;
    htmlContent: string;
    createdAt: ErpIsoDate;
    updatedAt: ErpIsoDate;
}
export interface ListErpActivityTemplatesResponse {
    data: ErpActivityTemplateResponse[];
}

export const erpSystemOwnedActivityTypes = [
    'timeline',
    'attachment',
    'document',
    'status_change',
    'positioning',
    'mission',
    'system',
] as const;
export type ErpSystemOwnedActivityType = (typeof erpSystemOwnedActivityTypes)[number];

export const erpActivityPriorities = ['low', 'normal', 'high', 'urgent'] as const;
export type ErpActivityPriority = (typeof erpActivityPriorities)[number];

export const erpActivityOutcomes = [
    'positive',
    'neutral',
    'negative',
    'no_answer',
    'to_follow_up',
    'interested',
    'not_interested',
    'won',
    'lost',
    'cancelled',
] as const;
export type ErpActivityOutcome = (typeof erpActivityOutcomes)[number];

export interface ErpListQuery {
    page?: string;
    limit?: string;
    sort?: 'date' | 'updatedAt' | 'priority' | 'status';
    direction?: 'asc' | 'desc';
    search?: string;
    sortBy?: string;
    sortOrder?: 'asc' | 'desc';
    status?: string;
    stage?: string;
    opportunityType?: string;
    contractType?: string;
    qualificationStatus?: string;
    salesStage?: string;
    forecastCategory?: string;
    lostReason?: string;
    seniority?: string;
    priority?: string;
    relationshipStatus?: string;
    owner?: string;
    ownerId?: string;
    assignedTo?: string;
    assignedToId?: string;
    recruiter?: string;
    recruiterId?: string;
    type?: string;
    outcome?: string;
    companyId?: string;
    resourceId?: string;
    contactId?: string;
    needId?: string;
    positioningId?: string;
    missionId?: string;
    documentId?: string;
    entityType?: string;
    entityId?: string;
    from?: string;
    to?: string;
    planningFrom?: string;
    planningTo?: string;
    dueBefore?: string;
    dueAfter?: string;
    includeArchived?: string;
    archivedOnly?: string;
    closeDateFrom?: string;
    closeDateTo?: string;
    deadlineBefore?: string;
    deadlineAfter?: string;
    nextFollowUpBefore?: string;
    nextFollowUpAfter?: string;
    minAmount?: string;
    maxAmount?: string;
    atRisk?: string;
    availabilityStatus?: string;
    availableFrom?: string;
    availableTo?: string;
    availableWithinDays?: string;
    includeUnavailable?: string;
    includeInMission?: string;
    staffingOwner?: string;
    staffingOwnerId?: string;
    unassigned?: string;
    remotePreference?: string;
    workloadPreference?: string;
    staffingPriority?: string;
    endingWithinDays?: string;
    missionStatus?: string;
    hasCurrentMission?: string;
    hasFutureMission?: string;
}

export interface ErpPaginatedResponse<T> {
    count: number;
    data: T[];
    page?: number;
    limit?: number;
}

export interface ErpBaseEntity {
    id: ErpId;
    createdAt: ErpIsoDate;
    updatedAt: ErpIsoDate;
    archivedAt: ErpIsoDate | null;
}

export interface ErpEntityVisibility {
    restricted?: boolean;
    allowedUserIds?: ErpId[];
    allowedTeamIds?: ErpId[];
}

export type ErpIdentityStatus = 'pending_verification' | 'active' | 'suspended' | 'disabled';
export type ErpWorkspaceStatus = 'provisioning' | 'active' | 'suspended' | 'pending_deletion' | 'deleted';
export type ErpWorkspaceMembershipStatus = 'invited' | 'active' | 'suspended' | 'disabled';

export interface ErpIdentityDto extends ErpBaseEntity {
    email: string;
    firstName: string;
    lastName: string;
    displayName?: string;
    contactEmail?: string;
    phone?: string;
    locale: string;
    timezone: string;
    dateFormat: string;
    mustChangePassword: boolean;
    emailVerifiedAt?: string | null;
    lastLoginAt?: string | null;
    active: boolean;
    status: ErpIdentityStatus;
    terms?: {
        termsVersion?: string;
        acceptedAt?: string;
        privacyVersion?: string;
        marketingConsent?: boolean;
        source?: string;
    };
}

export interface ErpWorkspaceDto extends ErpBaseEntity {
    name: string;
    slug: string;
    status: ErpWorkspaceStatus;
    platformCompanyId?: string | null;
    locale: string;
    timezone: string;
    currency: string;
    country?: string;
    ownerMembershipId?: ErpId | null;
    logoDocumentId?: ErpId | null;
    onboardingStatus: ErpWorkspaceOnboardingStatus;
    onboardingCompletedAt?: string | null;
    createdByIdentityId: ErpId;
    suspendedAt?: string | null;
    deletionRequestedAt?: string | null;
    deletedAt?: string | null;
    subscription?: ErpWorkspaceSubscription | null;
    settings?: Record<string, unknown>;
}

export type ErpWorkspaceSummaryDto = Pick<
    ErpWorkspaceDto,
    'id' | 'name' | 'slug' | 'status' | 'locale' | 'timezone' | 'currency' | 'ownerMembershipId'
>;

export interface ErpActionBarPreferences {
    pinnedActionIds: string[];
    hiddenActionIds: string[];
}

export const ERP_USER_CONFIGURABLE_DESKTOP_WIDGET_TYPES = [
    'dashboard',
    'activities',
    'gantt',
    'calendar',
    'notes',
    'kanban',
    'reporting',
    'companies',
    'contacts',
    'candidates',
    'consultants',
    'staffing',
    'needs',
    'positionings',
    'missions',
    'resource-dts',
] as const;

export type ErpUserConfigurableDesktopWidgetType = (typeof ERP_USER_CONFIGURABLE_DESKTOP_WIDGET_TYPES)[number];

export const ERP_DESKTOP_WIDGET_DEFAULT_OPEN_SURFACES = ['board', 'floating'] as const;
export type ErpDesktopWidgetDefaultOpenSurface = (typeof ERP_DESKTOP_WIDGET_DEFAULT_OPEN_SURFACES)[number];

export interface ErpDesktopWidgetPreferences {
    hiddenWidgetTypes: ErpUserConfigurableDesktopWidgetType[];
    defaultOpenSurface: ErpDesktopWidgetDefaultOpenSurface;
}

export interface ErpMembershipPreferences {
    actionBar?: ErpActionBarPreferences;
    desktopWidgets?: ErpDesktopWidgetPreferences;
}

export type UpdateErpDesktopWidgetPreferenceRequest =
    | {
          widgetType: ErpUserConfigurableDesktopWidgetType;
          visible: boolean;
      }
    | {
          defaultOpenSurface: ErpDesktopWidgetDefaultOpenSurface;
      };

export interface UpdateErpActionBarPreferencesRequest {
    pinnedActionIds?: string[];
    hiddenActionIds?: string[];
}

export interface ErpWorkspaceActionDiscoverySettings {
    promotedActionIds: string[];
}

export interface ErpActionUsageSummary {
    window: {
        from: string;
        to: string;
    };
    recent: Array<{
        actionId: string;
        lastExecutedAt: string;
    }>;
    frequent: Array<{
        actionId: string;
        executionCount: number;
        lastExecutedAt: string;
    }>;
}

export interface ErpWorkspaceMembershipDto extends ErpBaseEntity {
    workspaceId: ErpId;
    identityId: ErpId;
    roleId: ErpId;
    isOwner: boolean;
    status: ErpWorkspaceMembershipStatus;
    active: boolean;
    teamIds: ErpId[];
    managerMembershipId?: ErpId | null;
    resourceId?: ErpId | null;
    jobTitle?: string;
    department?: string;
    preferences?: ErpMembershipPreferences;
    customFields?: Record<string, unknown>;
    invitedByMembershipId?: ErpId | null;
    invitedAt?: string | null;
    joinedAt?: string | null;
    suspendedAt?: string | null;
}

export interface ErpCurrentContextDto {
    identityId: ErpId;
    membershipId: ErpId;
    workspaceId: ErpId;
    email: string;
    firstName: string;
    lastName: string;
    role: string;
    permissions: ErpPermissionAssignment[];
    isWorkspaceOwner: boolean;
    sessionId: string;
    id?: ErpId;
}

export interface CreateWorkspaceDto {
    name: string;
    slug?: string;
    locale?: string;
    timezone?: string;
    currency?: string;
    country?: string;
    platformCompanyId?: string;
}

export type UpdateWorkspaceDto = Partial<Pick<ErpWorkspaceDto, 'name' | 'logoDocumentId' | 'locale' | 'timezone' | 'currency' | 'country' | 'settings'>>;
export interface SwitchWorkspaceDto {
    workspaceId: ErpId;
}

export interface RegisterWorkspaceDto {
    workspace: CreateWorkspaceDto;
    owner: {
        email: string;
        password: string;
        firstName: string;
        lastName: string;
    };
    acceptedTerms: boolean;
    termsVersion: string;
    marketingConsent?: boolean;
}

export interface ErpWorkspaceSelectionItemDto {
    id: ErpId;
    name: string;
    slug: string;
    role: string;
    isOwner: boolean;
}

export interface ErpWorkspaceSelectionIdentityDto {
    firstName: string;
    lastName: string;
}

export interface ErpWorkspaceSelectionRequiredDto {
    requiresWorkspaceSelection: true;
    workspaces: ErpWorkspaceSelectionItemDto[];
    identity: ErpWorkspaceSelectionIdentityDto;
    selectionToken: string;
}

export interface VerifyEmailDto {
    token: string;
}
export interface ResendVerificationDto {
    email: string;
}
export interface SelectWorkspaceDto {
    selectionToken: string;
    workspaceId: ErpId;
}

export interface CreateWorkspaceInvitationDto {
    email: string;
    roleId: ErpId;
    teamIds?: ErpId[];
    managerMembershipId?: ErpId;
    firstName?: string;
    lastName?: string;
    jobTitle?: string;
    department?: string;
}

export interface AcceptWorkspaceInvitationDto {
    token: string;
    firstName?: string;
    lastName?: string;
    password?: string;
    acceptedTerms?: boolean;
    termsVersion?: string;
}

export interface WorkspaceInvitationPreviewDto {
    workspace: Pick<ErpWorkspaceDto, 'id' | 'name' | 'slug'>;
    role: { id: ErpId; key: string; name: string };
    email: string;
    expiresAt: string;
}

export type ErpTeamType = 'sales' | 'recruitment' | 'staffing' | 'practice' | 'agency' | 'business_unit' | 'other';

export interface ErpUserOption {
    /**
     * Compatibility alias for legacy /users/options consumers.
     * New assignment consumers must use membershipId.
     */
    id: ErpId;
    membershipId: ErpId;
    identityId: ErpId;
    legacyUserId?: ErpId;
    workspaceId: ErpId;
    displayName: string;
    email?: string;
    role?: string;
    active: boolean;
    status?: string;
    assignable: boolean;
    teamIds: ErpId[];
}

export interface ErpTeam extends ErpBaseEntity {
    name: string;
    code?: string;
    type: ErpTeamType;
    description?: string;
    managerId?: ErpId | ErpUserOption | null;
    memberIds: Array<ErpId | ErpUserOption>;
    active: boolean;
    color?: string;
    customFields?: Record<string, unknown>;
}

export type CreateErpTeamDto = Omit<ErpTeam, 'id' | 'createdAt' | 'updatedAt' | 'archivedAt' | 'managerId' | 'memberIds'> & {
    managerId?: ErpId | null;
    memberIds?: ErpId[];
};
export type UpdateErpTeamDto = Partial<Omit<CreateErpTeamDto, 'memberIds' | 'managerId'>>;

export type ErpOwnershipScope =
    | 'companies'
    | 'contacts'
    | 'opportunities'
    | 'resources'
    | 'positionings'
    | 'missions'
    | 'activities'
    | 'documents';
export type ErpOwnershipSummary = Record<ErpOwnershipScope, number>;
export interface ReassignErpUserDto {
    targetUserId: ErpId;
    scopes: ErpOwnershipScope[];
}
export interface ReassignErpUserResponse {
    updated: ErpOwnershipSummary;
}

export interface ErpResource extends ErpBaseEntity {
    type: ErpResourceType;
    status: ErpResourceStatus;
    identity?: ErpResourceIdentity;
    contactDetails?: ErpResourceContactDetails;
    organization?: ErpResourceOrganization;
    consultantProfile?: ErpResourceConsultantProfile;
    candidateProfile?: ErpResourceCandidateProfile;
    staffProfile?: ErpResourceStaffProfile;
    visibility?: ErpEntityVisibility;
    notes?: string;
}

export type CreateErpResourceDto = Omit<ErpResource, 'id' | 'createdAt' | 'updatedAt' | 'archivedAt'>;
export type UpdateErpResourceDto = Partial<CreateErpResourceDto>;

export * from './resource-dt';

export interface ErpResourceDtLink extends ErpBaseEntity {
    workspaceId: ErpId;
    resourceId: ErpId;
    dtId: ErpId;
    createdById?: ErpId | null;
}

export interface ErpResourceDtListItem extends ErpResourceDtLink {
    title: string;
    type?: 'dt-neylia' | 'mini-cv' | string;
    entityId?: string;
    entityType?: 'candidate' | 'resource' | string;
    language?: string;
    dtCreatedAt?: string | null;
    dtUpdatedAt?: string | null;
    navigation: {
        href: string;
        modulePath: string;
        query: {
            entityId: string;
            entityType: string;
            rightDt: string;
        };
    };
}

export interface ErpResourceDtLinkListResponse {
    count: number;
    data: ErpResourceDtListItem[];
}

export interface AttachErpResourceDtLinkDto {
    dtId: ErpId;
}

export interface ArchiveErpResourceDtLinkDto {
    dtId?: ErpId;
}

export interface ErpResourceAvailability {
    status?: ErpResourceAvailabilityStatus;
    date?: string | null;
    comment?: string;
    updatedAt?: string | null;
    mobility?: ErpReferentialReference[];
    override?: boolean;
    overrideReason?: string;
    remotePreference?: ErpResourceRemotePreference;
    workloadPreference?: ErpResourceWorkloadPreference;
    noticePeriodDays?: number | null;
    preferredMissionType?: string;
    preferredSectors?: string[];
    preferredRoles?: string[];
    lastStaffingReviewAt?: string | null;
    nextStaffingReviewAt?: string | null;
}

export interface ErpResourceConsultantProfile {
    availability?: ErpResourceAvailability;
    technicalFileDocumentIds?: ErpId[];
    contractDocumentIds?: ErpId[];
}

export interface ErpResourceCandidateProfile {
    source?: string;
    desiredContractType?: string;
    currentSalary?: number | null;
    desiredSalaryMin?: number | null;
    desiredSalaryMax?: number | null;
    technicalFileDocumentId?: ErpId | null;
}

export interface ErpResourceStaffProfile {
    role?: string;
    department?: string;
    managerResourceId?: ErpId | null;
    managerMembershipId?: ErpId | null;
}

export interface ErpResourceUnavailability extends ErpBaseEntity {
    resourceId: ErpId;
    type: ErpResourceUnavailabilityType;
    status: ErpResourceUnavailabilityStatus;
    startDate: string;
    endDate?: string | null;
    reason?: string;
    notes?: string;
    owner?: string;
    source?: string;
    externalRef?: string;
    customFields?: Record<string, unknown>;
}

export type CreateErpResourceUnavailabilityDto = Omit<ErpResourceUnavailability, 'id' | 'createdAt' | 'updatedAt' | 'archivedAt'>;
export type UpdateErpResourceUnavailabilityDto = Partial<CreateErpResourceUnavailabilityDto>;

export const erpEvaluationCriterionKeys = ['technical', 'communication', 'experience', 'motivation', 'mission_fit', 'other'] as const;
export type ErpEvaluationCriterionKey = (typeof erpEvaluationCriterionKeys)[number];

export interface ErpEvaluationCriterion {
    key: ErpEvaluationCriterionKey | string;
    label?: string;
    score: number;
    comment?: string | null;
}

export interface ErpEvaluation extends ErpBaseEntity {
    resourceId: ErpId;
    evaluatorMembershipId: ErpId;
    createdById: ErpId;
    updatedById?: ErpId | null;
    criteria: ErpEvaluationCriterion[];
    comment?: string | null;
}

export type CreateErpEvaluationDto = Pick<ErpEvaluation, 'criteria' | 'comment'>;
export type UpdateErpEvaluationDto = Partial<CreateErpEvaluationDto>;
export type ErpEvaluationListResponse = { count: number; data: ErpEvaluation[] };

export interface ErpContractReference {
    familyKey?: string;
    referentialId?: ErpId;
    valueId?: ErpId;
    key?: string;
    label: string;
    source?: 'neylia' | 'erp' | 'import' | 'manual' | 'other';
    rawValue?: string;
}

export interface ErpContract extends ErpBaseEntity {
    workspaceId?: ErpId | null;
    resourceId: ErpId;
    type?: ErpContractReference;
    status?: ErpContractReference;
    startDate: string;
    endDate?: string | null;
    comment?: string | null;
    mainDocumentId?: ErpId | null;
    signerUserIds: ErpId[];
    signerContactIds: ErpId[];
    parentContractId?: ErpId | null;
    rootContractId: ErpId;
    createdById: ErpId;
    updatedById?: ErpId | null;
}

export type CreateErpContractDto = Pick<
    ErpContract,
    'type' | 'status' | 'startDate' | 'endDate' | 'comment' | 'mainDocumentId' | 'signerUserIds' | 'signerContactIds'
>;
export type UpdateErpContractDto = Partial<CreateErpContractDto>;
export type CreateErpContractAmendmentDto = CreateErpContractDto;
export type ErpContractListResponse = { count: number; data: ErpContract[] };
export type ErpContractDetailResponse = ErpContract;

export interface ErpComputedResourceAvailability {
    availabilityStatus: ErpResourceAvailabilityStatus;
    availabilityDate?: string | null;
    availabilityReason?: string;
    currentMission?: ErpMission | null;
    nextMission?: ErpMission | null;
    currentUnavailability?: ErpResourceUnavailability | null;
    nextUnavailability?: ErpResourceUnavailability | null;
    missionEndDate?: string | null;
    daysUntilAvailable?: number | null;
    daysUntilMissionEnd?: number | null;
    isAvailableNow: boolean;
    isAvailableSoon: boolean;
    isInMission: boolean;
    isPartiallyAvailable: boolean;
    hasAvailabilityOverride: boolean;
    warnings: string[];
}

export interface ErpStaffingResource extends ErpResource {
    availability: ErpComputedResourceAvailability;
}

export interface ErpStaffingAlerts {
    missionEnd30: ErpStaffingResource[];
    missionEnd60: ErpStaffingResource[];
    missionEnd90: ErpStaffingResource[];
    unknownAvailability: ErpStaffingResource[];
    withoutMission: ErpStaffingResource[];
    activeUnavailabilities: ErpStaffingResource[];
    overdueReviews: ErpStaffingResource[];
    highPriority: ErpStaffingResource[];
}

export interface ErpCompanyAddress {
    line1?: string;
    line2?: string;
    zipCode?: string;
    city?: string;
    country?: string;
}

export interface ErpCompany extends ErpBaseEntity {
    status: ErpCompanyStatus;
    priority: ErpCompanyPriority;
    name: string;
    logoDocumentId?: ErpId | null;
    parentCompanyId?: ErpId | null;
    parentCompanyName?: string | null;
    domain?: string;
    siret?: string;
    rcs?: string;
    vatNumber?: string;
    legalStatus?: string;
    apeCode?: string;
    website?: string;
    linkedinUrl?: string;
    phone?: string;
    email?: string;
    address?: ErpCompanyAddress;
    contactDetails?: ErpContactDetails;
    organization?: ErpOrganization;
    visibility?: ErpEntityVisibility;
    accountOwner?: string;
    ownerId?: ErpId | null;
    industry?: string;
    sectorReferences?: ErpReferentialReference[];
    sectors?: ErpReferentialReference[];
    size?: string;
    headcount?: string;
    source?: string;
    annualRevenue?: number | null;
    revenue?: number | null;
    notes?: string;
    customFields?: Record<string, unknown>;
}

export type CreateErpCompanyDto = Omit<ErpCompany, 'id' | 'createdAt' | 'updatedAt' | 'archivedAt'>;
export type UpdateErpCompanyDto = Partial<CreateErpCompanyDto>;

export interface ErpContact extends ErpBaseEntity {
    companyId: ErpId;
    identity?: ErpResourceIdentity;
    contactDetails?: ErpContactDetails;
    organization?: ErpOrganization;
    visibility?: ErpEntityVisibility;
    firstName: string;
    lastName: string;
    civility?: string;
    photoDocumentId?: ErpId | null;
    email?: string;
    phone?: string;
    role?: string;
    jobTitle?: string;
    linkedinUrl?: string;
    decisionRole?: string;
    status?: string;
    type?: string;
    relationshipStatus: ErpContactRelationshipStatus;
    source?: string;
    owner?: string;
    ownerId?: ErpId | null;
    influencerUserIds?: ErpId[];
    agencyTeamId?: ErpId | null;
    businessUnitTeamId?: ErpId | null;
    address?: string;
    zipCode?: string;
    city?: string;
    country?: string;
    domains?: ErpReferentialReference[];
    tools?: ErpReferentialReference[];
    isPrimary: boolean;
    notes?: string;
    customFields?: Record<string, unknown>;
}

export type CreateErpContactDto = Omit<ErpContact, 'id' | 'createdAt' | 'updatedAt' | 'archivedAt' | 'companyId'>;
export type UpdateErpContactDto = Partial<CreateErpContactDto>;

export interface ErpNeed extends ErpBaseEntity {
    companyId?: ErpId | null;
    visibility?: ErpEntityVisibility;
    status: ErpNeedStatus;
    stage: ErpNeedStage;
    priority: ErpNeedPriority;
    title: string;
    description?: string;
    skills: string[];
    requiredDomains?: ErpReferentialReference[];
    requiredTools?: ErpReferentialReference[];
    targetSectors?: ErpReferentialReference[];
    location?: string;
    remoteMode?: string;
    startDate?: string | null;
    endDate?: string | null;
    budgetTjm?: number | null;
    amount?: number | null;
    probability?: number | null;
    closeDate?: string | null;
    opportunityType?: ErpNeedOpportunityType;
    contractType?: ErpNeedContractType;
    businessLine?: string;
    practice?: string;
    qualificationStatus?: ErpNeedQualificationStatus;
    qualificationScore?: number | null;
    qualificationNotes?: string;
    decisionProcess?: string;
    decisionCriteria?: string;
    competition?: string[];
    incumbentProvider?: string;
    budgetConfirmed?: boolean;
    timelineConfirmed?: boolean;
    decisionMakerIdentified?: boolean;
    headcount?: number | null;
    profilesSubmittedCount?: number | null;
    positionsFilledCount?: number | null;
    seniority?: ErpNeedSeniority;
    staffingOwner?: string;
    staffingOwnerId?: ErpId | null;
    recruiter?: string;
    recruiterId?: ErpId | null;
    profileSubmissionDeadline?: string | null;
    clientInterviewTargetDate?: string | null;
    clientDecisionDate?: string | null;
    salesStage?: ErpNeedSalesStage;
    forecastCategory?: ErpNeedForecastCategory;
    nextAction?: string;
    nextFollowUpAt?: string | null;
    lastInteractionAt?: string | null;
    proposalSentAt?: string | null;
    expectedDecisionAt?: string | null;
    lostReason?: ErpNeedLostReason;
    lostDetails?: string;
    pausedReason?: string;
    pausedUntil?: string | null;
    estimatedDurationDays?: number | null;
    estimatedWorkloadDays?: number | null;
    buyRate?: number | null;
    sellRate?: number | null;
    estimatedRevenue?: number | null;
    estimatedCost?: number | null;
    estimatedMargin?: number | null;
    estimatedMarginRate?: number | null;
    weightedAmount?: number | null;
    currency?: string;
    forecastStartDate?: string | null;
    forecastEndDate?: string | null;
    forecastNotes?: string;
    financialDataStatus?: ErpFinancialDataStatus;
    externalRef?: string;
    clientReference?: string;
    purchaseOrderExpected?: boolean;
    isLate?: boolean;
    isAtRisk?: boolean;
    daysUntilClose?: number | null;
    daysUntilProfileDeadline?: number | null;
    daysUntilNextFollowUp?: number | null;
    openPositionsCount?: number;
    pipelineAgeDays?: number | null;
    owner?: string;
    ownerId?: ErpId | null;
    source?: string;
    pointOfContactId?: ErpId | null;
    contactIds: ErpId[];
    notes?: string;
    customFields?: Record<string, unknown>;
}

export type CreateErpNeedDto = Omit<ErpNeed, 'id' | 'createdAt' | 'updatedAt' | 'archivedAt' | 'companyId'>;
export type UpdateErpNeedDto = Partial<CreateErpNeedDto>;

export interface ErpOpportunityPipelineStage {
    key: string;
    label: string;
    count: number;
    amount: number;
    weightedAmount: number;
    estimatedMargin: number;
    opportunities: ErpNeed[];
}

export interface ErpOpportunityPipeline {
    stages: ErpOpportunityPipelineStage[];
    totals: {
        count: number;
        amount: number;
        weightedAmount: number;
        estimatedRevenue: number;
        estimatedMargin: number;
        openPositionsCount: number;
        atRiskCount: number;
    };
}

export interface ErpOpportunitySummaryGroup {
    key: string;
    count: number;
    amount: number;
    weightedAmount: number;
    estimatedMargin: number;
}

export interface ErpOpportunitySummary {
    totalOpen: number;
    totalWon: number;
    totalLost: number;
    atRisk: number;
    dueThisWeek: number;
    closingThisMonth: number;
    weightedAmount: number;
    estimatedMargin: number;
    byStage: ErpOpportunitySummaryGroup[];
    byForecastCategory: ErpOpportunitySummaryGroup[];
    byOwner: ErpOpportunitySummaryGroup[];
    byOpportunityType: ErpOpportunitySummaryGroup[];
    byLostReason: ErpOpportunitySummaryGroup[];
}

export interface ErpCompanySummary {
    total: number;
    prospects: number;
    clients: number;
    unassigned: number;
}

export interface ErpContactSummary {
    total: number;
    primary: number;
    activeRelationships: number;
    unassigned: number;
}

export interface ErpResourceSummary {
    total: number;
    consultants: number;
    candidates: number;
    staff: number;
    available: number;
}

export interface ErpPositioningSummary {
    total: number;
    proposed: number;
    interviews: number;
    accepted: number;
}

export interface ErpMissionSummary {
    active: number;
    planned: number;
    endingSoon: number;
    unassigned: number;
}

export interface ErpPositioning extends ErpBaseEntity {
    resourceId: ErpId;
    needId: ErpId;
    companyId: ErpId;
    contactId?: ErpId | null;
    status: ErpPositioningStatus;
    owner?: string;
    ownerId?: ErpId | null;
    recruiter?: string;
    recruiterId?: ErpId | null;
    assignedTo?: string;
    assignedToId?: ErpId | null;
    proposedTjm?: number | null;
    buyRate?: number | null;
    sellRate?: number | null;
    estimatedMargin?: number | null;
    estimatedMarginRate?: number | null;
    sentAt?: string | null;
    interviewAt?: string | null;
    decisionAt?: string | null;
    expectedStartDate?: string | null;
    startDate?: string | null;
    clientFeedback?: string;
    resourceFeedback?: string;
    rejectionReason?: string;
    lossReason?: string;
    withdrawalReason?: string;
    matchingComment?: string;
    domainsSnapshot?: ErpReferentialReference[];
    toolsSnapshot?: ErpReferentialReference[];
    sectorsSnapshot?: ErpReferentialReference[];
    score?: number | null;
    cvUrl?: string;
    proposalUrl?: string;
    externalRef?: string;
    notes?: string;
    customFields?: Record<string, unknown>;
}

export type CreateErpPositioningDto = Omit<ErpPositioning, 'id' | 'createdAt' | 'updatedAt' | 'archivedAt'>;
export type UpdateErpPositioningDto = Partial<Omit<CreateErpPositioningDto, 'resourceId' | 'needId' | 'companyId'>>;

export interface ErpMission extends ErpBaseEntity {
    resourceId: ErpId;
    companyId: ErpId;
    needId?: ErpId | null;
    positioningId?: ErpId | null;
    contactId?: ErpId | null;
    status: ErpMissionStatus;
    title: string;
    description?: string;
    domains?: ErpReferentialReference[];
    tools?: ErpReferentialReference[];
    sectors?: ErpReferentialReference[];
    role?: string;
    location?: string;
    remoteMode?: string;
    startDate: string;
    endDate?: string | null;
    expectedEndDate?: string | null;
    renewalDate?: string | null;
    workload?: string;
    purchaseOrderRef?: string;
    externalRef?: string;
    buyRate?: number | null;
    sellRate?: number | null;
    margin?: number | null;
    marginRate?: number | null;
    currency?: string;
    billingMode?: ErpMissionBillingMode;
    plannedWorkloadDays?: number | null;
    actualWorkloadDays?: number | null;
    workloadPercentage?: number | null;
    workingDaysPerWeek?: number | null;
    estimatedRevenue?: number | null;
    estimatedCost?: number | null;
    estimatedGrossMargin?: number | null;
    estimatedMarginRate?: number | null;
    actualRevenue?: number | null;
    actualCost?: number | null;
    actualGrossMargin?: number | null;
    actualMarginRate?: number | null;
    actualUpdatedAt?: string | null;
    actualSource?: ErpMissionActualSource;
    renewalProbability?: number | null;
    renewalExpectedStartDate?: string | null;
    renewalExpectedEndDate?: string | null;
    renewalEstimatedRevenue?: number | null;
    renewalNotes?: string;
    financialDataStatus?: ErpFinancialDataStatus;
    financialNotes?: string;
    owner?: string;
    ownerId?: ErpId | null;
    recruiter?: string;
    recruiterId?: ErpId | null;
    staffingOwner?: string;
    staffingOwnerId?: ErpId | null;
    notes?: string;
    customFields?: Record<string, unknown>;
}

export type CreateErpMissionDto = Omit<ErpMission, 'id' | 'createdAt' | 'updatedAt' | 'archivedAt'>;
export type UpdateErpMissionDto = Partial<CreateErpMissionDto>;

export interface ErpFinancialWarning {
    code: string;
    field?: string;
    message: string;
}

export interface ErpFinancialForecastQuery {
    from?: string;
    to?: string;
    currency?: string;
    companyId?: ErpId;
    owner?: string;
    recruiter?: string;
    staffingOwner?: string;
    missionStatus?: ErpMissionStatus;
    opportunityStage?: string;
    forecastCategory?: ErpNeedForecastCategory;
    sourceType?: ErpForecastSourceType;
    includeOpportunities?: boolean;
    includeMissions?: boolean;
    includeRenewals?: boolean;
    includeAdjustments?: boolean;
    includeActuals?: boolean;
    groupBy?: 'month' | 'company' | 'owner' | 'mission' | 'opportunity' | 'forecastCategory' | 'sourceType';
    page?: string;
    limit?: string;
}

export interface ErpFinancialForecastLine {
    period: string;
    sourceType: ErpForecastSourceType;
    sourceId: ErpId;
    title: string;
    companyId?: ErpId | null;
    companyName?: string;
    owner?: string;
    status?: string;
    forecastCategory?: string;
    revenue: number;
    weightedRevenue: number;
    cost: number;
    margin: number;
    marginRate: number | null;
    actualRevenue: number;
    actualCost: number;
    actualMargin: number;
    actualMarginRate: number | null;
    calculationMethod: ErpForecastCalculationMethod;
    currency: string;
    warnings: ErpFinancialWarning[];
}

export interface ErpFinancialForecastSummary {
    period: { from: string; to: string };
    currency: string;
    totals: {
        forecastRevenue: number;
        weightedRevenue: number;
        committedRevenue: number;
        actualRevenue: number;
        forecastCost: number;
        actualCost: number;
        forecastMargin: number;
        actualMargin: number;
        forecastMarginRate: number | null;
        actualMarginRate: number | null;
        revenueVariance: number;
        costVariance: number;
        marginVariance: number;
    };
    counts: {
        missions: number;
        opportunities: number;
        renewals: number;
        incompleteRecords: number;
    };
}

export interface ErpMonthlyFinancialForecast {
    month: string;
    missionRevenue: number;
    opportunityRevenue: number;
    weightedOpportunityRevenue: number;
    renewalRevenue: number;
    adjustmentRevenue: number;
    forecastRevenue: number;
    actualRevenue: number;
    forecastCost: number;
    actualCost: number;
    forecastMargin: number;
    actualMargin: number;
    forecastMarginRate: number | null;
    actualMarginRate: number | null;
}

export interface ErpFinancialForecastMonthlyResponse {
    months: ErpMonthlyFinancialForecast[];
}

export interface ErpFinancialForecastBreakdownResponse {
    count: number;
    data: ErpFinancialForecastLine[];
}

export interface ErpFinancialDataQualityResponse {
    missionsWithoutSellRate: ErpMission[];
    missionsWithoutBuyRate: ErpMission[];
    missionsWithoutEndDate: ErpMission[];
    missionsWithoutWorkload: ErpMission[];
    opportunitiesWithoutAmount: ErpNeed[];
    opportunitiesWithoutProbability: ErpNeed[];
    opportunitiesWithoutPeriod: ErpNeed[];
    negativeMarginRecords: ErpFinancialForecastLine[];
    inconsistentRecords: ErpFinancialForecastLine[];
    wonOpportunitiesWithoutMission: ErpNeed[];
    duplicateForecastRisks: ErpFinancialForecastLine[];
}

export interface ErpActivity extends ErpBaseEntity {
    /** Canonical business links. An Activity may be standalone or linked to many entities. */
    relations: ErpActivityRelation[];
    type: ErpActivityType;
    category?: string;
    priority: ErpActivityPriority;
    outcome?: ErpActivityOutcome;
    title?: string;
    content?: string;
    /** @deprecated Read compatibility projection for records written before content became canonical. */
    body?: string;
    author?: ErpActivityAuthor;
    links: ErpActivityLink[];
    attachmentIds: ErpId[];
    provenance?: string;
    nextAction?: string;
    nextActionAt?: string | null;
    occurredAt?: string | null;
    scheduledAt?: string | null;
    completedAt?: string | null;
    remindAt?: string | null;
    owner?: string;
    ownerId?: ErpId | null;
    assignedTo?: string;
    assignedToId?: ErpId | null;
    createdById?: ErpId | null;
    updatedById?: ErpId | null;
    participants: string[];
    contactIds: ErpId[];
    resourceIds: ErpId[];
    /** @deprecated Legacy scalar relation projection. Use relations instead. */
    companyId?: ErpId | null;
    /** @deprecated Legacy scalar relation projection. Use relations instead. */
    contactId?: ErpId | null;
    /** @deprecated Legacy scalar relation projection. Use relations instead. */
    resourceId?: ErpId | null;
    /** @deprecated Legacy scalar relation projection. Use relations instead. */
    needId?: ErpId | null;
    /** @deprecated Legacy scalar relation projection. Use relations instead. */
    positioningId?: ErpId | null;
    /** @deprecated Legacy scalar relation projection. Use relations instead. */
    missionId?: ErpId | null;
    /** @deprecated Legacy scalar relation projection. Use relations instead. */
    documentId?: ErpId | null;
    dueAt?: string | null;
    externalRef?: string;
    source?: string;
    tags: string[];
    metadata?: Record<string, unknown>;
    customFields?: Record<string, unknown>;
    isSystemGenerated: boolean;
    systemEvent?: string;
    previousValue?: string;
    newValue?: string;
    /** @deprecated Legacy scalar relation projection. Use relations instead. */
    entityType?: ErpActivityEntityType | string;
    /** @deprecated Legacy scalar relation projection. Use relations instead. */
    entityId?: ErpId;
}

export type CreateErpActivityDto = Omit<
    ErpActivity,
    | 'id'
    | 'createdAt'
    | 'updatedAt'
    | 'archivedAt'
    | 'relations'
    | 'links'
    | 'attachmentIds'
    | 'priority'
    | 'participants'
    | 'contactIds'
    | 'resourceIds'
    | 'tags'
    | 'metadata'
    | 'customFields'
    | 'isSystemGenerated'
> &
    Partial<
        Pick<
            ErpActivity,
            'priority' | 'participants' | 'contactIds' | 'resourceIds' | 'tags' | 'metadata' | 'customFields' | 'isSystemGenerated' | 'relations' | 'links' | 'attachmentIds'
        >
    >;
export type UpdateErpActivityDto = Partial<CreateErpActivityDto>;

export interface ErpDocument extends ErpBaseEntity {
    title: string;
    description?: string;
    type: ErpDocumentType;
    category?: string;
    status: ErpDocumentStatus;
    entityType: ErpDocumentEntityType;
    entityId: ErpId;
    companyId?: ErpId | null;
    resourceId?: ErpId | null;
    contactId?: ErpId | null;
    needId?: ErpId | null;
    positioningId?: ErpId | null;
    missionId?: ErpId | null;
    activityId?: ErpId | null;
    url?: string;
    storageProvider: ErpDocumentStorageProvider;
    storageKey?: string;
    originalFilename?: string;
    filename?: string;
    mimeType?: string;
    size?: number | null;
    extension?: string;
    checksum?: string;
    version?: string;
    isLatest: boolean;
    expiresAt?: string | null;
    signedAt?: string | null;
    owner?: string;
    ownerId?: ErpId | null;
    uploadedBy?: string;
    createdById?: ErpId | null;
    updatedById?: ErpId | null;
    source?: string;
    externalRef?: string;
    tags: string[];
    notes?: string;
    customFields?: Record<string, unknown>;
}

export const erpWorkEntityTypes = ['opportunity', 'resource', 'mission', 'activity', 'follow_up', 'positioning', 'document'] as const;
export type ErpWorkEntityType = (typeof erpWorkEntityTypes)[number];
export const ERP_WORK_SECTIONS = [
    'opportunities',
    'resources',
    'missions',
    'activities',
    'followUps',
    'positionings',
    'documents',
] as const;
export type ErpWorkSectionName = (typeof ERP_WORK_SECTIONS)[number];

export interface ErpWorkQuery {
    from?: string;
    to?: string;
    teamId?: ErpId;
    memberId?: ErpId;
    entityType?: ErpWorkEntityType;
    section?: ErpWorkSectionName;
    status?: string;
    priority?: string;
    overdue?: boolean;
    unassigned?: boolean;
    page?: string;
    limit?: string;
}

export interface ErpWorkItem {
    id: ErpId;
    entityType: ErpWorkEntityType;
    title: string;
    subtitle?: string;
    status?: string;
    priority?: string;
    date?: string | null;
    ownerId?: ErpId | null;
    ownerName?: string;
    overdue: boolean;
    unassigned: boolean;
    href: string;
}

export interface ErpWorkTimelineItem extends ErpWorkItem {
    date: string;
}

export interface ErpWorkSummaryCards {
    overdueTasks: number;
    opportunitiesToFollowUp: number;
    availableResources: number;
    missionsEndingSoon: number;
    renewals: number;
    weightedRevenue: number | null;
    forecastMargin: number | null;
}

export interface ErpBusinessReportingSections {
    needs?: {
        opportunities: number;
        openOpportunities: number;
        atRiskOpportunities: number;
    };
    resources?: {
        total: number;
        candidates: number;
        consultants: number;
        staff: number;
        available: number;
    };
    positionings?: { count: number };
    missions?: { active: number };
    staffing?: {
        availableNow: number;
        available30: number;
        available90: number;
    };
    financial?: {
        forecastRevenueThisMonth: number;
        weightedRevenueThisMonth: number;
        committedRevenue: number;
        forecastMarginThisMonth: number;
        pipelineAmount: number;
        weightedPipelineAmount: number;
        pipelineEstimatedMargin: number;
    };
}

export interface ErpBusinessDashboardResponse {
    sections: ErpBusinessReportingSections;
}

export interface ErpWorkSectionCounts {
    opportunities: number;
    resources: number;
    missions: number;
    activities: number;
    followUps: number;
    positionings: number;
    documents: number;
    overdue: number;
    unassigned: number;
}

export interface ErpWorkPermissions {
    canViewTeam: boolean;
    canViewUnassigned: boolean;
    canViewForecast: boolean;
    canViewMargin: boolean;
}

export interface ErpTeamWorkMember {
    id: ErpId;
    displayName: string;
    opportunities: number;
    resources: number;
    activeMissions: number;
    overdueActivities: number;
    workloadPercentage: number;
}

export interface ErpWorkSummary {
    mode: 'me' | 'team';
    period: { from: string; to: string };
    cards: ErpWorkSummaryCards;
    sections: ErpWorkSectionCounts;
    permissions: ErpWorkPermissions;
    timeline: ErpWorkTimelineItem[];
    members: ErpTeamWorkMember[];
    alerts: {
        missionEnds: number;
        expectedRenewals: number;
        incompleteFinancialData: number | null;
    };
}

export interface ErpWorkSection<T> {
    count: number;
    total: number;
    page: number;
    limit: number;
    pageSize: number;
    hasMore: boolean;
    data: T[];
}

export type ErpWorkItemsResponse = ErpWorkSection<ErpWorkItem>;
export type ErpWorkSectionsResponse = Record<ErpWorkSectionName, ErpWorkSection<ErpWorkItem>>;

export type CreateErpDocumentDto = Omit<
    ErpDocument,
    | 'id'
    | 'createdAt'
    | 'updatedAt'
    | 'archivedAt'
    | 'status'
    | 'storageProvider'
    | 'storageKey'
    | 'originalFilename'
    | 'filename'
    | 'mimeType'
    | 'size'
    | 'extension'
    | 'checksum'
    | 'isLatest'
    | 'tags'
> &
    Partial<Pick<ErpDocument, 'status' | 'storageProvider' | 'isLatest' | 'tags'>>;
export type UpdateErpDocumentDto = Partial<CreateErpDocumentDto>;
